/**
 * Permission Selector Component
 * 
 * Displays permissions organized by category with checkboxes.
 * Follows UI/UX guidelines for form inputs and accessibility.
 */

import { Checkbox, Badge } from '@/components/ui';
import type { PermissionCategory } from '@/services/RolesService';

interface PermissionSelectorProps {
  categories: PermissionCategory[];
  selectedPermissions: string[];
  onChange: (permissions: string[]) => void;
  disabled?: boolean;
}

export function PermissionSelector({
  categories,
  selectedPermissions,
  onChange,
  disabled = false,
}: PermissionSelectorProps) {
  const handleToggle = (permission: string) => {
    if (selectedPermissions.includes(permission)) {
      onChange(selectedPermissions.filter((p) => p !== permission));
    } else {
      onChange([...selectedPermissions, permission]);
    }
  };

  const handleToggleAll = (categoryPermissions: string[]) => {
    const allSelected = categoryPermissions.every((p) =>
      selectedPermissions.includes(p)
    );

    if (allSelected) {
      // Deselect all in this category
      onChange(
        selectedPermissions.filter((p) => !categoryPermissions.includes(p))
      );
    } else {
      // Select all in this category
      const newPermissions = new Set([
        ...selectedPermissions,
        ...categoryPermissions,
      ]);
      onChange(Array.from(newPermissions));
    }
  };

  return (
    <div className="space-y-6">
      {categories.map((category) => {
        const categoryPermissions = category.permissions.map((p) => p.value);
        const allSelected = categoryPermissions.every((p) =>
          selectedPermissions.includes(p)
        );
        const someSelected =
          categoryPermissions.some((p) => selectedPermissions.includes(p)) &&
          !allSelected;

        return (
          <div key={category.category} className="space-y-3">
            {/* Category Header with Select All */}
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <h4 className="text-sm font-semibold text-foreground">
                {category.category}
              </h4>
              <button
                type="button"
                onClick={() => handleToggleAll(categoryPermissions)}
                disabled={disabled}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Permission Checkboxes */}
            <div className="space-y-2 pl-1">
              {category.permissions.map((permission) => (
                <label
                  key={permission.value}
                  className="flex items-start gap-3 cursor-pointer group"
                >
                  <Checkbox
                    checked={selectedPermissions.includes(permission.value)}
                    onChange={() => handleToggle(permission.value)}
                    disabled={disabled}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground group-hover:text-foreground">
                        {permission.label}
                      </span>
                      {permission.granularOf && (
                        <Badge variant="secondary">
                          Granular
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {permission.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

